import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Archive,
  Loader2,
  Plus,
  Star,
  Warehouse as WarehouseIcon,
} from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useArchiveWarehouse,
  useCreateWarehouse,
  useWarehouses,
} from "@/lib/api/inventory.queries";
import { updateWarehouse } from "@/lib/api/inventory";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute(
  "/companies/$companyId/inventario/bodegas",
)({
  component: BodegasPage,
});

function BodegasPage() {
  return (
    <RequireAuth>
      <AppShell>
        <BodegasContent />
      </AppShell>
    </RequireAuth>
  );
}

function BodegasContent() {
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [includeInactive, setIncludeInactive] = useState(false);
  const warehousesQuery = useWarehouses(companyId, { includeInactive });
  const createWh = useCreateWarehouse(companyId);
  const archiveWh = useArchiveWarehouse(companyId);

  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  function reset() {
    setCode("");
    setName("");
    setAddress("");
    setIsDefault(false);
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <Link
            to="/companies/$companyId/inventario"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            ← Volver a Inventario
          </Link>
          <div className="flex items-center gap-2">
            <WarehouseIcon className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight">Bodegas</h1>
          </div>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Una bodega por ubicación física o lógica. La bodega marcada como
            <strong> predeterminada</strong> recibe los movimientos
            automáticos de ventas, compras y notas cuando la línea no
            especifica una bodega.
          </p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)}>
          <Plus className="h-4 w-4" />
          {showForm ? "Cerrar" : "Nueva bodega"}
        </Button>
      </header>

      {showForm && (
        <Card>
          <CardContent className="flex flex-col gap-3 py-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Código</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="SUC1"
                  pattern="[A-Z0-9_-]+"
                  maxLength={20}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Mayúsculas, dígitos, &quot;-&quot; y &quot;_&quot;. Único por
                  empresa.
                </p>
              </div>
              <div>
                <Label>Nombre</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Sucursal Norte"
                />
              </div>
            </div>
            <div>
              <Label>Dirección (opcional)</Label>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Calle 123 #45-67"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
              Marcar como bodega predeterminada
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowForm(false);
                  reset();
                }}
              >
                Cancelar
              </Button>
              <Button
                disabled={createWh.isPending || !code || !name}
                onClick={() => {
                  createWh.mutate(
                    {
                      code,
                      name,
                      address: address || undefined,
                      isDefault,
                    },
                    {
                      onSuccess: () => {
                        toast.success("Bodega creada");
                        reset();
                        setShowForm(false);
                      },
                      onError: (err) =>
                        toast.error("No pudimos crear la bodega", {
                          description: err.message,
                        }),
                    },
                  );
                }}
              >
                {createWh.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Crear bodega
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <label className="flex w-fit items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={includeInactive}
          onChange={(e) => setIncludeInactive(e.target.checked)}
        />
        Incluir bodegas archivadas
      </label>

      {warehousesQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : warehousesQuery.isError ? (
        <ErrorState
          title="No pudimos cargar las bodegas"
          description={warehousesQuery.error?.message ?? ""}
          onRetry={() => void warehousesQuery.refetch()}
          retryLabel="Reintentar"
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Dirección</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(warehousesQuery.data?.warehouses ?? []).map((w) => (
                <TableRow key={w.id}>
                  <TableCell>
                    <Badge variant="muted" className="font-mono">
                      {w.code}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{w.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {w.address ?? "—"}
                  </TableCell>
                  <TableCell className="space-x-2">
                    {w.isDefault && (
                      <Badge variant="default">
                        <Star className="h-3 w-3" />
                        Predeterminada
                      </Badge>
                    )}
                    {w.isActive ? (
                      !w.isDefault && (
                        <Badge variant="success">Activa</Badge>
                      )
                    ) : (
                      <Badge variant="muted">Archivada</Badge>
                    )}
                  </TableCell>
                  <TableCell className="space-x-1 text-right">
                    {w.isActive && !w.isDefault && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            try {
                              await updateWarehouse(companyId, w.id, {
                                isDefault: true,
                              });
                              toast.success(
                                `${w.code} ahora es la bodega predeterminada.`,
                              );
                              await warehousesQuery.refetch();
                            } catch (err) {
                              toast.error("No pudimos actualizar", {
                                description: (err as Error).message,
                              });
                            }
                          }}
                        >
                          <Star className="h-3 w-3" />
                          Hacer predeterminada
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          disabled={archiveWh.isPending}
                          onClick={() => {
                            if (
                              !confirm(
                                `¿Archivar la bodega "${w.name}"? No podrás archivarla si tiene existencias positivas.`,
                              )
                            )
                              return;
                            archiveWh.mutate(w.id, {
                              onSuccess: () =>
                                toast.success("Bodega archivada"),
                              onError: (err) =>
                                toast.error("No pudimos archivar", {
                                  description: err.message,
                                }),
                            });
                          }}
                        >
                          <Archive className="h-3 w-3" />
                          Archivar
                        </Button>
                      </>
                    )}
                    {w.isActive && w.isDefault && (
                      <span className="text-xs text-muted-foreground">
                        Marca otra como predeterminada para archivarla.
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(warehousesQuery.data?.warehouses ?? []).length === 0 && (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <WarehouseIcon className="h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Sin bodegas. Crea la primera para empezar a registrar
                movimientos.
              </p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
